import {
  WebBlock,
  WebInputChoice,
  WebModalState,
} from "@p0security/shared/integrations/notifiers/web-requests/types";
import { Flex, Image, Typography } from "antd";
import { groupBy } from "lodash";
import { FieldErrors, UseControllerProps } from "react-hook-form";
import { spacing } from "styles/spacing";

export const MAX_SELECT_OPTIONS = 10;

// TODO: return any because antd's select parameter `options` is not typed correctly.
export const createOptionGroups = (options: WebInputChoice[]): any => {
  const groups = groupBy(options, (option) => option.group);
  const groupKeys = Object.keys(groups);

  if (!groupKeys.filter(Boolean).length) {
    return options.map((option) => ({
      label: <WebInputChoiceLabel {...option} />,
      value: option.value,
      filterLabel: option.label,
    }));
  }

  const groupValues = Object.values(groups);

  return groupKeys.map((key, index) => ({
    label: key,
    title: key,
    options: groupValues[index].map((option) => ({
      label: <WebInputChoiceLabel {...option} />,
      value: option.value,
      // Join the group key with the option label to create a unique filter label
      // that can be used for filtering in the Select component. This allows
      // users to search for options by both group and label.
      filterLabel: `${key}-${option.label}`,
    })),
  }));
};

const WebInputChoiceLabel: React.FC<WebInputChoice> = (option) => {
  return (
    <Flex align="center" gap="small">
      <WebInputChoiceIcon {...option} />
      <Typography.Text ellipsis>{option.label}</Typography.Text>
    </Flex>
  );
};

const WebInputChoiceIcon: React.FC<WebInputChoice> = ({ icon, label }) => {
  if (!icon) return null;
  return (
    <Image
      src={icon}
      alt={label}
      width={spacing.scale.lg}
      style={{ marginRight: spacing.scale.sm }}
    />
  );
};

export const filterDispatchBlocks = (block: WebBlock) => {
  if ("dispatch" in block && block.dispatch) {
    return block.id;
  }
  return undefined;
};

export const getBlockValues = (blocks: WebBlock[]) => {
  return blocks.reduce((acc, block) => {
    if ("value" in block) {
      acc[block.id] = block.value;
    }
    return acc;
  }, {} as WebModalState);
};

export const createFormRules = (block: WebBlock) => {
  const rules: UseControllerProps["rules"] = {};
  if ("required" in block && block.required) {
    rules.required = {
      value: true,
      message: "This field is required",
    };
  }
  if ("multivalued" in block && block.multivalued) {
    rules.validate = {
      validate: (value: string[]) => {
        if (value.length > MAX_SELECT_OPTIONS) {
          return "Too many options selected";
        }
        return true;
      },
    };
  }
  return rules;
};

export const parseFormError = (error: FieldErrors[number]): string => {
  if (!error) return "";

  if (!error.message) {
    console.error("Error message is missing", error);
    return "An unexpected error occurred";
  }

  if (typeof error.message === "string") {
    return error.message;
  }

  console.error("Error message is not a string", error);
  return "An unexpected error occurred";
};
